import { Dropdown, Pivot, PivotItem, Separator, Stack, StackItem, TextField } from '@fluentui/react';
import { observer } from 'mobx-react';
import { ProvisionProfileEditorState } from './ProvisionProfileEditor';

import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import { ProvisionType, SecurityMode, SharingCapabilities } from '../../../API';
import ExpressionField from '../expression-builder/ExpressionField';
import EntityMultiColumnSelector from '../entity-types/EntityMultiColumnSelector';
import NewPeoplePicker, { PeoplePickerIdFormat } from '../../controls/NewPeoplePicker';

function ProfileWorkspaceSecurity({ state }: { state: ProvisionProfileEditorState }) {
	

	let applicableType = '';
	if(state.ProvisionType === ProvisionType.Site) applicableType = 'site';
	else if(state.ProvisionType === ProvisionType.Team) applicableType = 'unifiedGroup';


	let applicableLabels = (state.sensitivityLabels || []).filter(a => (a.applicableTo || '').indexOf(applicableType) > -1).map(s => s.name);

	return (<>
			<Stack>

				<Stack horizontal>
					{ state.ProvisionType === ProvisionType.Site && <>
						<StackItem>
							<div style={{marginRight:10}}>
								<InfoTooltipLabel label='Sharing capabilities' title={`Select the sharing capabilities for this workspace.`}>
									<Dropdown
										style={{width:350}}
										options={[
											{ key: SharingCapabilities.Disabled, text: 'Disabled' },
											{ key: SharingCapabilities.ExistingExternalUserSharingOnly, text: 'Allow existing external users' },
											{ key: SharingCapabilities.ExternalUserSharingOnly, text: 'Allow new & existing external users' },
											{ key: SharingCapabilities.ExternalUserAndGuestSharing, text: 'Allow new & existing external users, and guests' }
										]}
										selectedKey={state.SiteSharingCapabilities}
										onChange={(e, option) => { state.setSiteSharingCapabilities(option?.key as SharingCapabilities); }}
									/>
								</InfoTooltipLabel>
							</div>
						</StackItem>
					</> }

					{(state.ProvisionType === ProvisionType.Site || state.ProvisionType === ProvisionType.Team) && <>
						<StackItem>
								<InfoTooltipLabel 
									label='Sensitivity Label'
									title={`Enter an expression that resolves to the sensitivity label name or ID to apply to the site or team. Leave blank to skip applying a sensitivity label.` + (applicableLabels.length > 0 ? ' Available sensitivity labels for this workspace type: ' + applicableLabels.join(', ') : '')}>

									<TextField
										style={{width:350}}
										value={state.SensitivityLabelExpression}
										onChange={(_, newValue) => state.setSensitivityLabelExpression(newValue || '')}
										placeholder='e.g., {{Item.SensitivityLabel}} or a static label name'
									/>
								</InfoTooltipLabel>
						</StackItem>
					</>}
				</Stack>


				<Pivot style={{marginTop:20}}>
					<PivotItem headerText='Workspace'>
							<InfoTooltipLabel 
								label='Security sync mode'
								title='Determines if and how security is configured on your workspace. None: No security sync. OneOff: Security synced only when workspace is first created. SyncToWorkspace: Security synced on every change.'>
								<Dropdown
									options={[
										{ key: SecurityMode.None, text: 'Do not set security' },
										{ key: SecurityMode.OneOff, text: 'Set security once, on initial workspace creation' },
										{ key: SecurityMode.SyncFromMetadata, text: 'Keep workspace security synchronised with metadata' }
									]}
									selectedKey={state.SecurityMode}
									onChange={(e, option) => { state.setSecurityMode(option?.key as SecurityMode); }}
								/>
							</InfoTooltipLabel>
							{ state.SecurityMode !== SecurityMode.None ?
							<>

								<Separator />


								{ state.ProvisionType === ProvisionType.Team && <>
									
									<InfoTooltipLabel label='Team privacy' title={`Select an expression which will be used to drive whether or not the Team is private.`}>
										<ExpressionField state={state.privacyExpressionState} />
									</InfoTooltipLabel>
								
								</>}

								{ (state.ProvisionType === ProvisionType.Library || state.ProvisionType === ProvisionType.Folder )
									&& <>

									<InfoTooltipLabel label='Apply security only when' title={`Select an expression which will be used to drive whether or not the workspace is secured.`}>
										<ExpressionField state={state.privacyExpressionState} />
									</InfoTooltipLabel>
								
								</>}
								
								{ (state.ProvisionType === ProvisionType.TeamChannel )
									&& <>

									<InfoTooltipLabel label='Set as private channel when' title={`Select an expression which will be used to drive whether or not the channel is a private channel.`}>
										<ExpressionField state={state.privacyExpressionState} />
									</InfoTooltipLabel>
								
								</>}

								<Stack horizontal grow>

									{/* Ensure the stackitem has a fixed width of 50% */}
									<StackItem grow={50}>

										<InfoTooltipLabel label='Owner fields' title={`Workspace owners will be controlled via the selected people field values.`}>
											<EntityMultiColumnSelector state={state.ownerFields}/>
										</InfoTooltipLabel>

										<InfoTooltipLabel label='Member fields' title={`Workspace members will be controlled via the selected people field values.`}>
											<EntityMultiColumnSelector state={state.memberFields}/>
										</InfoTooltipLabel>


										{ state.ProvisionType !== ProvisionType.Team && <>
											<InfoTooltipLabel label='Visitor fields' title={`Workspace visitors will be controlled via the selected people field values.`}>
												<EntityMultiColumnSelector state={state.visitorFields}/>
											</InfoTooltipLabel>
										</>}
									</StackItem>


									<StackItem grow>

										<InfoTooltipLabel label='Static owners' title={`These owners will always be added to the workspace.`}>
											<NewPeoplePicker format={PeoplePickerIdFormat.UPN} siteUrl={state.listUrl} config={state.userConfig} initialPeople={state.SecurityOwners} updatePeople={(people) => state.setSecurityOwners(people)} />
										</InfoTooltipLabel>

										<InfoTooltipLabel label='Static members' title={`These members will always be added to the workspace.`}>
											<NewPeoplePicker format={PeoplePickerIdFormat.UPN} siteUrl={state.listUrl} config={state.userConfig} initialPeople={state.SecurityMembers} updatePeople={(people) => state.setSecurityMembers(people)} />
										</InfoTooltipLabel>


										{ state.ProvisionType !== ProvisionType.Team && <>
											<InfoTooltipLabel label='Static visitors' title={`These visitors will always be added to the workspace.`}>
												<NewPeoplePicker format={PeoplePickerIdFormat.UPN} siteUrl={state.listUrl} config={state.userConfig} initialPeople={state.SecurityVisitors} updatePeople={(people) => state.setSecurityVisitors(people)} />
											</InfoTooltipLabel>
										</>}
									</StackItem>

								</Stack>
						</> : <></>}

					</PivotItem>
					<PivotItem headerText='Metadata'>
							<>
					<InfoTooltipLabel 
						label='Security sync mode'
						title='Determines if and how security is configured on your metadata. None: No security sync. OneOff: Security synced only when item is first created. SyncFromMetadata: Security synced on every change.'>
						<Dropdown
							options={[
								{ key: SecurityMode.None, text: 'Do not set security' },
								{ key: SecurityMode.OneOff, text: 'Set list item security once, on initial item creation' },
								{ key: SecurityMode.SyncFromMetadata, text: 'Keep list item security synchronised with metadata values' }
							]}
							selectedKey={state.MetadataSecurityMode}
							onChange={(e, option) => { state.setMetadataSecurityMode(option?.key as SecurityMode); }}
						/>
					</InfoTooltipLabel>
				
					{ state.MetadataSecurityMode === SecurityMode.None ? <></> : <>
						<Separator />

			
						<InfoTooltipLabel label='Secure metadata condition' title={`Select an expression which will be used to drive whether or not the item is secured.`}>
							<ExpressionField state={state.metadataPrivacyExpressionState} />
						</InfoTooltipLabel>

						<Stack horizontal grow>
							<StackItem grow={50}>
								<InfoTooltipLabel label='Item owner fields' title={`List item/metadata owners will be controlled via the selected people field values. Files will be organized into folders based on these security settings.`}>
									<EntityMultiColumnSelector state={state.metadataSecurityOwnerFields}/>
								</InfoTooltipLabel>

								<InfoTooltipLabel label='Item member fields' title={`List item/metadata members will be controlled via the selected people field values.`}>
									<EntityMultiColumnSelector state={state.metadataSecurityMemberFields}/>
								</InfoTooltipLabel>

								<InfoTooltipLabel label='Item visitor fields' title={`List item/metadata visitors will be controlled via the selected people field values.`}>
									<EntityMultiColumnSelector state={state.metadataSecurityVisitorFields}/>
								</InfoTooltipLabel>
							</StackItem>

							<StackItem grow>
								<InfoTooltipLabel label='Static item owners' title={`These owners will always be added to list items/metadata.`}>
									<NewPeoplePicker format={PeoplePickerIdFormat.UPN} siteUrl={state.listUrl} config={state.userConfig} initialPeople={state.MetadataSecurityOwners} updatePeople={(people) => state.setMetadataSecurityOwners(people)} />
								</InfoTooltipLabel>

								<InfoTooltipLabel label='Static item members' title={`These members will always be added to list items/metadata.`}>
									<NewPeoplePicker format={PeoplePickerIdFormat.UPN} siteUrl={state.listUrl} config={state.userConfig} initialPeople={state.MetadataSecurityMembers} updatePeople={(people) => state.setMetadataSecurityMembers(people)} />
								</InfoTooltipLabel>

								<InfoTooltipLabel label='Static item visitors' title={`These visitors will always be added to list items/metadata.`}>
									<NewPeoplePicker format={PeoplePickerIdFormat.UPN} siteUrl={state.listUrl} config={state.userConfig} initialPeople={state.MetadataSecurityVisitors} updatePeople={(people) => state.setMetadataSecurityVisitors(people)} />
								</InfoTooltipLabel>
							</StackItem>
						</Stack>
					</>}
				</>
					</PivotItem>
				</Pivot>

				
				
			</Stack>
		</>
	);
}

export default observer(ProfileWorkspaceSecurity);
